import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { verifyTurnstile } from '~/apis/turnstileApi';
import { loadTurnstileScript } from '~/lib/turnstile/loadTurnstileScript';

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

type TurnstileGateProps = {
  children: React.ReactNode;
  storageKey?: string;
  ttlMs?: number;
  action?: string;
};

function readPassTimestamp(storageKey: string): number | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts?: unknown };
    const ts = typeof parsed?.ts === 'number' ? parsed.ts : null;
    return ts;
  } catch {
    return null;
  }
}

function writePassTimestamp(storageKey: string, ts: number): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ts }));
  } catch {
    // ignore (storage disabled)
  }
}

export function TurnstileGate({
  children,
  storageKey = 'turnstile:cn01-form:passedAt',
  ttlMs = DEFAULT_TTL_MS,
  action,
}: TurnstileGateProps) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITEKEY;

  const [passed, setPassed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  const alreadyValid = useMemo(() => {
    const ts = readPassTimestamp(storageKey);
    if (!ts) return false;
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) return true;
    return Date.now() - ts < ttlMs;
  }, [storageKey, ttlMs]);

  const cleanupWidget = useCallback(() => {
    inFlightRef.current = false;
    const id = widgetIdRef.current;
    if (!id) return;
    try {
      window.turnstile?.remove?.(id);
    } catch {
      // ignore
    } finally {
      widgetIdRef.current = null;
    }
  }, []);

  const run = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setLoading(true);
    setError(null);

    try {
      if (!siteKey) {
        throw new Error('Turnstile sitekey is not configured.');
      }

      await loadTurnstileScript();
      if (!window.turnstile) {
        throw new Error('Turnstile failed to initialize.');
      }

      if (!widgetContainerRef.current) {
        throw new Error('Turnstile mount node is missing.');
      }

      cleanupWidget();

      const widgetId = window.turnstile.render(widgetContainerRef.current, {
        sitekey: siteKey,
        action,
        appearance: 'interaction-only',
        callback: async (token: string) => {
          try {
            await verifyTurnstile(token);
            writePassTimestamp(storageKey, Date.now());
            setPassed(true);
          } catch (e) {
            const msg =
              e instanceof Error && e.message ? e.message : 'Turnstile verification failed.';
            setError(msg);
            setPassed(false);
            try {
              window.turnstile?.reset?.(widgetId);
            } catch {
              // ignore
            }
          } finally {
            setLoading(false);
            inFlightRef.current = false;
          }
        },
        'error-callback': () => {
          setError(
            'Human verification could not be completed due to a temporary issue. Please refresh the page or try again later.'
          );
          setPassed(false);
          setLoading(false);
          inFlightRef.current = false;
        },
        'expired-callback': () => {
          setError('Turnstile expired. Please retry.');
          setPassed(false);
          setLoading(false);
          inFlightRef.current = false;
        },
      });

      widgetIdRef.current = widgetId;
    } catch (e) {
      const msg = e instanceof Error && e.message ? e.message : 'Unable to run Turnstile.';
      setError(msg);
      setPassed(false);
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [action, cleanupWidget, siteKey, storageKey]);

  useEffect(() => {
    if (alreadyValid) {
      setPassed(true);
      setLoading(false);
      return;
    }

    void run();
    return () => cleanupWidget();
  }, [alreadyValid, cleanupWidget, run]);

  if (passed) return <>{children}</>;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={(theme) => ({
          width: '100%',
          maxWidth: 520,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          p: 3,
        })}
      >
        <Stack spacing={1.25}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Verifying you’re human
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This protects the public page from automated abuse.
          </Typography>

          <Box sx={{ mt: 0.5 }}>
            {loading ? (
              <Stack direction="row" spacing={1.25} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Checking…</Typography>
              </Stack>
            ) : error ? (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            ) : null}
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              disabled={loading}
              onClick={() => {
                void run();
              }}
              sx={{ borderRadius: 2, minWidth: 120 }}
            >
              Retry
            </Button>
          </Stack>
        </Stack>

        {/* Invisible widget mounts here */}
        <Box ref={widgetContainerRef} sx={{ mt: 2, minHeight: 1 }} />
      </Box>
    </Box>
  );
}
